import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Calendar as CalendarIcon, Trash2, Loader2, CheckCircle2,
    HelpCircle, ExternalLink, ChevronRight, Apple as AppleIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider quick-open: opens the relevant calendar settings page so the
// user just has to copy the iCal URL from there. iCloud has no public
// settings deep-link, so we route Apple users straight to the help page.
const QUICK_OPEN = {
    google: {
        label_de: 'Google Kalender öffnen',
        label_en: 'Open Google Calendar',
        url: 'https://calendar.google.com/calendar/u/0/r/settings/calendar',
        emoji: '🟦',
    },
    outlook: {
        label_de: 'Outlook öffnen',
        label_en: 'Open Outlook',
        url: 'https://outlook.live.com/calendar/0/options/calendar/SharedCalendars',
        emoji: '🟧',
    },
};

export function CalendarSubscriptionCard() {
    const { language } = useLanguage();
    const isDe = language === 'de';
    const [url, setUrl] = useState('');
    const [configured, setConfigured] = useState(false);
    const [eventCount, setEventCount] = useState(0);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/calendar/upcoming`);
                if (cancelled) return;
                if (res.data?.configured) {
                    setConfigured(true);
                    setEvents(res.data.events || []);
                    setEventCount((res.data.events || []).length);
                }
            } catch (_e) { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleRefresh = async () => {
        setSaving(true);
        try {
            const res = await axios.get(`${API}/calendar/upcoming`, { params: { refresh: true } });
            setEvents(res.data.events || []);
            setEventCount((res.data.events || []).length);
            toast.success(isDe ? 'Termine aktualisiert' : 'Events refreshed', { duration: 3000 });
        } catch (_e) {
            toast.error(isDe ? 'Aktualisierung fehlgeschlagen' : 'Refresh failed');
        } finally { setSaving(false); }
    };

    const handleSave = async () => {
        if (!url.trim()) return;
        setSaving(true);
        try {
            const res = await axios.post(`${API}/calendar/config`, { ical_url: url.trim() });
            setConfigured(true);
            setEventCount(res.data?.events_found_on_validation || 0);
            toast.success(isDe ? '📅 Kalender verbunden' : '📅 Calendar connected', { duration: 4000 });
            try {
                const up = await axios.get(`${API}/calendar/upcoming`);
                setEvents(up.data.events || []);
                setEventCount((up.data.events || []).length);
            } catch (_e) { /* silent */ }
        } catch (e) {
            const detail = e?.response?.data?.detail || (isDe ? 'Ungültige Kalender-URL' : 'Invalid calendar URL');
            toast.error(detail, { duration: 4000 });
        } finally { setSaving(false); }
    };

    const handleRemove = async () => {
        setSaving(true);
        try {
            await axios.delete(`${API}/calendar/config`);
            setConfigured(false);
            setUrl('');
            setEventCount(0);
            setEvents([]);
            toast.success(isDe ? 'Kalender entfernt' : 'Calendar removed', { duration: 4000 });
        } catch (_e) {
            toast.error(isDe ? 'Entfernen fehlgeschlagen' : 'Remove failed', { duration: 4000 });
        } finally { setSaving(false); }
    };

    const handlePasteFromClipboard = async () => {
        if (!navigator.clipboard || !navigator.clipboard.readText) {
            toast.info(
                isDe
                    ? 'Diese Browser-Version erlaubt kein automatisches Einfügen. Bitte den Link manuell einfügen.'
                    : 'This browser does not support clipboard read. Please paste the link manually.',
                { duration: 4000 },
            );
            return;
        }
        try {
            const text = await navigator.clipboard.readText();
            if (text && text.trim()) {
                setUrl(text.trim());
                toast.success(isDe ? 'Link eingefügt' : 'Link pasted', { duration: 2000 });
            } else {
                toast.error(isDe ? 'Zwischenablage ist leer' : 'Clipboard is empty');
            }
        } catch (_e) {
            toast.error(isDe ? 'Zugriff auf Zwischenablage verweigert' : 'Clipboard access denied');
        }
    };

    if (loading) return null;

    return (
        <Card data-testid="calendar-subscription-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-primary" />
                    {isDe ? 'Kalender-Sync (optional)' : 'Calendar sync (optional)'}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {configured ? (
                    <>
                        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span data-testid="calendar-status">
                                {isDe
                                    ? `Verbunden · ${eventCount} kommende Termine`
                                    : `Connected · ${eventCount} upcoming events`}
                            </span>
                        </div>
                        {events.length > 0 && (
                            <div className="space-y-1" data-testid="calendar-event-preview">
                                {events.slice(0, 5).map((ev) => {
                                    const d = new Date(ev.start);
                                    const dateStr = d.toLocaleDateString(isDe ? 'de-CH' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'numeric' });
                                    const timeStr = ev.all_day
                                        ? (isDe ? 'ganztägig' : 'all day')
                                        : d.toLocaleTimeString(isDe ? 'de-CH' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
                                    return (
                                        <div key={`${ev.start}-${ev.summary}`} className="flex items-center gap-2 text-xs">
                                            <span className="text-muted-foreground shrink-0 w-24 tabular-nums">{dateStr} · {timeStr}</span>
                                            <span className="truncate font-medium">{ev.summary}</span>
                                        </div>
                                    );
                                })}
                                {events.length > 5 && (
                                    <p className="text-[10px] text-muted-foreground">
                                        {isDe ? `+ ${events.length - 5} weitere in den nächsten 14 Tagen` : `+ ${events.length - 5} more in the next 14 days`}
                                    </p>
                                )}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            {isDe
                                ? 'Angezeigt werden alle Termine (inkl. Serientermine) der nächsten 14 Tage aus deinem verbundenen Kalender. Nanouk kann sich darauf beziehen ("vor dem Meeting noch ein Spaziergang?").'
                                : 'Shows all events (incl. recurring) from your connected calendar for the next 14 days. Nanouk can reference them ("a walk before that meeting?").'}
                        </p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={saving} data-testid="calendar-refresh-btn" className="gap-2">
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                {isDe ? 'Aktualisieren' : 'Refresh'}
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleRemove} disabled={saving} data-testid="calendar-remove-btn" className="gap-2">
                                <Trash2 className="w-3.5 h-3.5" />
                                {isDe ? 'Verbindung entfernen' : 'Disconnect'}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Visual 3-step flow */}
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="space-y-1.5 p-2 rounded-lg bg-muted/40" data-testid="calendar-step-1">
                                <div className="w-7 h-7 rounded-full bg-violet-500/15 border border-violet-500/30 mx-auto flex items-center justify-center text-xs font-bold text-violet-600 dark:text-violet-400">1</div>
                                <p className="text-[10.5px] leading-snug text-muted-foreground">
                                    {isDe ? 'Anbieter wählen' : 'Pick provider'}
                                </p>
                            </div>
                            <div className="space-y-1.5 p-2 rounded-lg bg-muted/40" data-testid="calendar-step-2">
                                <div className="w-7 h-7 rounded-full bg-violet-500/15 border border-violet-500/30 mx-auto flex items-center justify-center text-xs font-bold text-violet-600 dark:text-violet-400">2</div>
                                <p className="text-[10.5px] leading-snug text-muted-foreground">
                                    {isDe ? 'Link kopieren' : 'Copy the link'}
                                </p>
                            </div>
                            <div className="space-y-1.5 p-2 rounded-lg bg-muted/40" data-testid="calendar-step-3">
                                <div className="w-7 h-7 rounded-full bg-violet-500/15 border border-violet-500/30 mx-auto flex items-center justify-center text-xs font-bold text-violet-600 dark:text-violet-400">3</div>
                                <p className="text-[10.5px] leading-snug text-muted-foreground">
                                    {isDe ? 'Hier einfügen' : 'Paste here'}
                                </p>
                            </div>
                        </div>

                        {/* One-click provider open buttons */}
                        <div className="space-y-1.5">
                            <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                                {isDe ? 'Schritt 1 · Anbieter öffnen' : 'Step 1 · Open your provider'}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                                <a
                                    href={QUICK_OPEN.google.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-violet-500/40 transition-colors text-xs"
                                    data-testid="calendar-open-google"
                                >
                                    <span className="flex items-center gap-2">
                                        <span aria-hidden="true">{QUICK_OPEN.google.emoji}</span>
                                        Google
                                    </span>
                                    <ExternalLink className="w-3 h-3 text-muted-foreground" />
                                </a>
                                <Link
                                    to="/help#calendar"
                                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-violet-500/40 transition-colors text-xs"
                                    data-testid="calendar-open-apple"
                                >
                                    <span className="flex items-center gap-2">
                                        <AppleIcon className="w-3 h-3" />
                                        Apple
                                    </span>
                                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                </Link>
                                <a
                                    href={QUICK_OPEN.outlook.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-card hover:border-violet-500/40 transition-colors text-xs"
                                    data-testid="calendar-open-outlook"
                                >
                                    <span className="flex items-center gap-2">
                                        <span aria-hidden="true">{QUICK_OPEN.outlook.emoji}</span>
                                        Outlook
                                    </span>
                                    <ExternalLink className="w-3 h-3 text-muted-foreground" />
                                </a>
                            </div>
                            <p className="text-[10px] text-muted-foreground leading-snug pt-0.5">
                                {isDe
                                    ? 'Apple Kalender hat keinen direkten Link — tap auf Apple oben für die genaue Anleitung.'
                                    : 'Apple Calendar has no direct deep-link — tap Apple above for the exact steps.'}
                            </p>
                        </div>

                        {/* Paste field */}
                        <div className="space-y-1.5">
                            <Label htmlFor="ical-url" className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                                {isDe ? 'Schritt 3 · Link hier einfügen' : 'Step 3 · Paste your link'}
                            </Label>
                            <div className="flex gap-1.5">
                                <Input
                                    id="ical-url"
                                    type="url"
                                    placeholder="webcal://… or https://….ics"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    data-testid="ical-url-input"
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handlePasteFromClipboard}
                                    data-testid="calendar-paste-btn"
                                    className="text-xs flex-shrink-0"
                                >
                                    {isDe ? 'Einfügen' : 'Paste'}
                                </Button>
                            </div>
                        </div>

                        <Button onClick={handleSave} disabled={saving || !url.trim()} data-testid="calendar-save-btn" className="gap-2 w-full">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarIcon className="w-4 h-4" />}
                            {isDe ? 'Verbinden' : 'Connect'}
                        </Button>

                        {/* Help link */}
                        <Link
                            to="/help#calendar"
                            className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors py-1"
                            data-testid="calendar-open-help"
                        >
                            <HelpCircle className="w-3 h-3" />
                            {isDe ? 'Detaillierte Anleitung für deinen Anbieter ansehen' : 'See detailed guide for your provider'}
                        </Link>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
